import { useRef, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';

import { classMerge } from 'utils/classMerge';

import { IconMetavrseLogo } from 'icons';

import { useEventListener } from 'hooks/useEventListener';
import { usePreventContextMenu } from 'hooks/usePreventContextMenu';

import Button from 'common/Button/Button';
import FormInput from 'common/FormInput/FormInput';
import Heading2 from 'common/Heading2/Heading2';
import ProgressBar from 'common/ProgressBar/ProgressBar';

import { publishValidation } from 'validations/publishValidation';

import FramePortal from '../_viewer/Viewer/EditorViewer/FrameComponent';

import FullscreenButton from './FullscreenButton/FullscreenButton';
import { usePublishedProjectLogic } from './hooks/usePublishedProjectLogic';
import { PublishedProjectErrors } from './hooks/usePublishedProjectLogic/errors';
import { usePublishHeroShot } from './hooks/usePublishHeroShot';
import classes from './ViewPublishedProject.module.css';

const ViewPublishedProject: React.FC = () => {
  const formRef = useRef<FormikProps<{ password?: string }>>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const viewerBoxRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { error, isLoading, isDownloading, loadingPercentage, loadProject } =
    usePublishedProjectLogic(canvasRef, formRef);
  const heroShot = usePublishHeroShot();

  useEventListener('fullscreenchange', (e) => {
    setIsFullscreen(Boolean(document.fullscreenElement));
  });
  usePreventContextMenu();

  const onButtonClick = () => {
    if (!isFullscreen) {
      viewerBoxRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
    document.activeElement.blur();
  };

  return (
    <div
      ref={viewerBoxRef}
      >
      <div
        className={classMerge(classes.overlay, {
          [classes.hidden]: !error && !isLoading && loadingPercentage === 100,
        })}
      >
        {error !== PublishedProjectErrors.INCORRECT_PASSWORD && (
          <Heading2>{error}</Heading2>
        )}
        {error === PublishedProjectErrors.INCORRECT_PASSWORD && (
          <div className={classes.formWrapper}>
            <Formik
              {...{
                initialValues: {
                  password: '',
                },
                validationSchema: publishValidation,
                validateOnMount: false,
                onSubmit: ({ password }: { password?: string }) => {
                  loadProject(password);
                },
              }}
              innerRef={formRef}
            >
              <Form>
                <FormInput name="password" label="Password" />
                <Button variant="primary" type="submit">
                  Load project
                </Button>
              </Form>
            </Formik>
          </div>
        )}

        {(isLoading || isDownloading) && (
          <>
            <img src={heroShot} alt="" className={classes.heroShot} />
            <ProgressBar
              section={{
                content: (isDownloading) ? 'Downloading project' : 'Loading project',
                innerSectionProgressValue: loadingPercentage,
                finishedSectionProgress: 100,
              }}
            />
          </>
        )}
      </div>
      <div
        className={classes.viewerBox}
        style={{ padding: '0' }}
      >
        <IconMetavrseLogo
          className={classes.headerLogo}
          color="var(--primaryVariant)"
        />
        <FullscreenButton onClick={onButtonClick} />
        {/*It may seem that these two divs are redundant, but actually they are required to properly calculate and render project when orientation was changed. */}
        <FramePortal>
          <div style={{ height: '100%' }} tabIndex={0}>
            <div className={classes.canvasWrapper} style={{ height: '100%' }}>
              <canvas
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                id="viewer"
                ref={canvasRef}
                tabIndex={0}
                className={classMerge(
                  classes.viewer,
                  classes.noselect,
                  'cherry-canvas'
                )}
                draggable="false"
              />
            </div>
          </div>
        </FramePortal>
      </div>
    </div>
  );
};

export default ViewPublishedProject;
